/**
 * Vercel Blob Storage Service - Migrated from Infura IPFS
 * Provides file upload functionality with SHA-256 hashing and comprehensive logging
 *
 * Key Features:
 * - SHA-256 content hashing (similar to IPFS CID)
 * - Dual upload strategy: Server-side (<4MB) and Client-side (>4MB)
 * - Comprehensive logging for monitoring and debugging
 * - Development fallback to localStorage
 * - Airtable metadata storage (replaces IPFS metadata)
 */

import airtableService from './airtable';

/**
 * Logger utility for consistent, structured logging
 */
const logger = {
  info: (component, message, data = {}) => {
    console.log(`[${component}] ℹ️  ${message}`, {
      ...data,
      timestamp: new Date().toISOString()
    });
  },
  success: (component, message, data = {}) => {
    console.log(`[${component}] ✅ ${message}`, {
      ...data,
      timestamp: new Date().toISOString()
    });
  },
  error: (component, message, error = {}) => {
    console.error(`[${component}] ❌ ${message}`, {
      error: error.message || error,
      stack: error.stack,
      timestamp: new Date().toISOString()
    });
  },
  warn: (component, message, data = {}) => {
    console.warn(`[${component}] ⚠️  ${message}`, {
      ...data,
      timestamp: new Date().toISOString()
    });
  },
  progress: (component, message, data = {}) => {
    console.log(`[${component}] 🔄 ${message}`, {
      ...data,
      timestamp: new Date().toISOString()
    });
  }
};

class BlobStorageService {
  constructor() {
    // API endpoint for upload operations
    this.apiEndpoint = process.env.REACT_APP_API_BASE_URL || window.location.origin;
    logger.info('BlobStorageService', 'Initialized', { apiEndpoint: this.apiEndpoint });
  }

  /**
   * Generate SHA-256 hash of file content
   * This provides content-based addressing similar to IPFS CIDs
   */
  async generateFileHash(file) {
    const startTime = Date.now();
    logger.progress('HASH', 'Generating SHA-256 hash', {
      fileName: file.name,
      fileSize: file.size
    });

    try {
      const buffer = await file.arrayBuffer();
      const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

      const duration = Date.now() - startTime;
      logger.success('HASH', 'SHA-256 hash generated', {
        hash: hashHex,
        duration: `${duration}ms`,
        fileName: file.name
      });

      return hashHex;
    } catch (error) {
      logger.error('HASH', 'Failed to generate hash', error);
      throw error;
    }
  }

  /**
   * Upload a file to Vercel Blob Storage
   * Strategy: Server-side upload for files <4MB, client-side for larger files
   */
  async uploadFile(file) {
    const startTime = Date.now();
    logger.info('UPLOAD', 'Starting file upload', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      readableSize: this.formatFileSize(file.size)
    });

    // Use local storage fallback in development mode
    if (this.isDevelopmentMode()) {
      logger.warn('UPLOAD', 'Using development mode with localStorage fallback');
      return this.uploadFileLocal(file);
    }

    try {
      // Generate SHA-256 hash for the file
      const fileHash = await this.generateFileHash(file);

      // Determine upload strategy based on file size
      const sizeThreshold = 4 * 1024 * 1024; // 4MB
      const useClientSide = file.size > sizeThreshold;

      logger.info('UPLOAD', 'Upload strategy determined', {
        strategy: useClientSide ? 'Client-side direct upload' : 'Server-side upload',
        fileSize: file.size,
        threshold: sizeThreshold,
        hash: fileHash
      });

      // For files larger than 4MB, use client-side direct upload
      if (useClientSide) {
        return await this.uploadFileClientSide(file, fileHash);
      }

      // For smaller files, use the server-side API route
      return await this.uploadFileServerSide(file, fileHash);

    } catch (error) {
      const duration = Date.now() - startTime;
      logger.error('UPLOAD', 'Upload failed', {
        ...error,
        fileName: file.name,
        duration: `${duration}ms`
      });

      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Server-side upload via API route (for files < 4MB)
   */
  async uploadFileServerSide(file, fileHash) {
    const startTime = Date.now();
    logger.progress('UPLOAD-SERVER', 'Starting server-side upload', {
      fileName: file.name,
      fileSize: file.size,
      hash: fileHash
    });

    try {
      // Prepare form data
      const formData = new FormData();
      formData.append('file', file);
      formData.append('fileHash', fileHash);
      formData.append('fileName', file.name);
      formData.append('contentType', file.type);

      logger.info('UPLOAD-SERVER', 'Sending request to API', {
        endpoint: `${this.apiEndpoint}/api/upload-blob`
      });

      // Upload to Vercel Blob via API route
      const response = await fetch(`${this.apiEndpoint}/api/upload-blob`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Upload failed: ${response.statusText}`);
      }

      const result = await response.json();
      const duration = Date.now() - startTime;

      logger.success('UPLOAD-SERVER', 'Upload completed successfully', {
        blobUrl: result.url,
        hash: fileHash,
        size: file.size,
        duration: `${duration}ms`
      });

      // Save metadata to Airtable
      const blobUrl = result.url || result.blobUrl;
      const airtableResult = await airtableService.createContentFile({
        fileName: file.name,
        fileHash: fileHash,
        blobUrl: blobUrl,
        fileType: this.getFileType(file.type),
        mimeType: file.type || 'application/octet-stream',
        fileSize: file.size,
        accessLevel: 'Authenticated',
        status: 'Active',
        projectName: 'Simple Upload - Vercel Blob'
      });

      if (airtableResult.success) {
        logger.success('UPLOAD-SERVER', 'Metadata saved to Airtable', {
          recordId: airtableResult.data.id,
          fileName: file.name
        });
      } else if (!airtableResult.skipped) {
        logger.warn('UPLOAD-SERVER', 'Failed to save metadata to Airtable', {
          error: airtableResult.error
        });
      }

      return {
        success: true,
        fileHash: fileHash,
        blobUrl: blobUrl,
        url: blobUrl,
        path: fileHash,
        size: file.size,
        originalFilename: file.name,
        hashFilename: `${fileHash}${this.getFileExtension(file.name)}`,
        ipfsGatewayUrl: blobUrl, // For backward compatibility
        uploadDuration: `${duration}ms`,
        airtableRecordId: airtableResult.success ? airtableResult.data.id : null
      };
    } catch (error) {
      const duration = Date.now() - startTime;
      logger.error('UPLOAD-SERVER', 'Server-side upload failed', {
        ...error,
        duration: `${duration}ms`
      });
      throw error;
    }
  }

  /**
   * Client-side direct upload to Vercel Blob (for files > 4MB)
   */
  async uploadFileClientSide(file, fileHash) {
    const startTime = Date.now();
    logger.progress('UPLOAD-CLIENT', 'Starting client-side direct upload', {
      fileName: file.name,
      fileSize: file.size,
      readableSize: this.formatFileSize(file.size),
      hash: fileHash
    });

    try {
      logger.info('UPLOAD-CLIENT', 'Loading @vercel/blob/client module');

      // Dynamically import @vercel/blob client
      const { upload } = await import('@vercel/blob/client');

      logger.info('UPLOAD-CLIENT', 'Initiating direct upload to Vercel Blob Storage');

      // Use relative URL to avoid CORS issues
      const blob = await upload(file.name, file, {
        access: 'public',
        handleUploadUrl: '/api/get-upload-url',
      });

      const duration = Date.now() - startTime;

      logger.success('UPLOAD-CLIENT', 'Client-side upload completed', {
        blobUrl: blob.url,
        hash: fileHash,
        size: file.size,
        duration: `${duration}ms`
      });

      // Save metadata to Airtable
      const airtableResult = await airtableService.createContentFile({
        fileName: file.name,
        fileHash: fileHash,
        blobUrl: blob.url,
        fileType: this.getFileType(file.type),
        mimeType: file.type || 'application/octet-stream',
        fileSize: file.size,
        accessLevel: 'Authenticated',
        status: 'Active',
        projectName: 'Simple Upload - Vercel Blob'
      });

      if (airtableResult.success) {
        logger.success('UPLOAD-CLIENT', 'Metadata saved to Airtable', {
          recordId: airtableResult.data.id,
          fileName: file.name
        });
      } else if (!airtableResult.skipped) {
        logger.warn('UPLOAD-CLIENT', 'Failed to save metadata to Airtable', {
          error: airtableResult.error
        });
      }

      return {
        success: true,
        fileHash: fileHash,
        blobUrl: blob.url,
        url: blob.url,
        path: fileHash,
        size: file.size,
        originalFilename: file.name,
        hashFilename: `${fileHash}${this.getFileExtension(file.name)}`,
        ipfsGatewayUrl: blob.url, // For backward compatibility
        uploadDuration: `${duration}ms`,
        airtableRecordId: airtableResult.success ? airtableResult.data.id : null
      };
    } catch (error) {
      const duration = Date.now() - startTime;
      logger.error('UPLOAD-CLIENT', 'Client-side upload failed', {
        ...error,
        duration: `${duration}ms`
      });
      throw error;
    }
  }

  /**
   * Development fallback: Store in browser localStorage
   * ONLY used in development mode when BLOB_READ_WRITE_TOKEN is not available
   */
  async uploadFileLocal(file) {
    const startTime = Date.now();
    logger.warn('UPLOAD-LOCAL', 'Using localStorage fallback (DEVELOPMENT ONLY)', {
      fileName: file.name,
      fileSize: file.size
    });

    try {
      // Generate hash
      const fileHash = await this.generateFileHash(file);

      // Read file as base64
      const base64 = await this.fileToBase64(file);
      logger.info('UPLOAD-LOCAL', 'Converted file to base64', {
        base64Length: base64.length,
        hash: fileHash
      });

      // Store in localStorage
      const storageKey = `file_${fileHash}`;
      const fileData = {
        name: file.name,
        type: file.type,
        size: file.size,
        data: base64,
        hash: fileHash,
        uploadDate: new Date().toISOString()
      };

      localStorage.setItem(storageKey, JSON.stringify(fileData));

      // Generate local URL
      const localUrl = `${window.location.origin}/blob/${fileHash}`;
      const duration = Date.now() - startTime;

      logger.success('UPLOAD-LOCAL', 'Stored in localStorage', {
        storageKey,
        localUrl,
        hash: fileHash,
        duration: `${duration}ms`
      });

      return {
        success: true,
        fileHash: fileHash,
        blobUrl: localUrl,
        url: localUrl,
        path: fileHash,
        size: file.size,
        originalFilename: file.name,
        hashFilename: `${fileHash}${this.getFileExtension(file.name)}`,
        ipfsGatewayUrl: localUrl,
        uploadDuration: `${duration}ms`,
        isDevelopment: true
      };
    } catch (error) {
      const duration = Date.now() - startTime;
      logger.error('UPLOAD-LOCAL', 'localStorage upload failed', {
        ...error,
        duration: `${duration}ms`
      });
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Helper: Convert file to base64
   */
  fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Check if running in development mode
   */
  isDevelopmentMode() {
    const isDev = process.env.NODE_ENV === 'development' ||
                  window.location.hostname === 'localhost' ||
                  window.location.hostname === '127.0.0.1';
    return isDev;
  }

  /**
   * Extract file extension from filename
   */
  getFileExtension(filename) {
    const lastDot = filename.lastIndexOf('.');
    return lastDot !== -1 ? filename.substring(lastDot) : '';
  }

  /**
   * Get file type category from MIME type
   * Returns capitalized types to match 01-1d1-content-server format
   */
  getFileType(mimeType) {
    if (!mimeType) return 'Other';

    if (mimeType.startsWith('image/')) return 'Image';
    if (mimeType.startsWith('video/')) return 'Video';
    if (mimeType.startsWith('audio/')) return 'Audio';
    if (mimeType === 'application/pdf') return 'PDF';
    if (mimeType.startsWith('text/')) return 'Document';
    if (mimeType.includes('json') || mimeType.includes('javascript') || mimeType.includes('xml')) return 'JSON';
    if (mimeType.includes('word') || mimeType.includes('document')) return 'Document';
    if (mimeType.includes('sheet') || mimeType.includes('excel')) return 'Spreadsheet';
    if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return 'Presentation';
    if (mimeType.includes('zip') || mimeType.includes('archive') || mimeType.includes('compressed')) return 'Archive';

    return 'Other';
  }

  /**
   * Format file size to human-readable format
   */
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }
}

// Export singleton instance
const blobStorageService = new BlobStorageService();
export default blobStorageService;

// Named exports for convenience
export { BlobStorageService };
